"use client";

import { AirVent as Lungs, Stethoscope, Moon, Syringe as Inhaler, Activity, Hospital, Sparkles } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurClinicalFocus() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const smallCards = [{
    icon: Stethoscope,
    title: "Pulmonary Consultations",
    link: "/pulmonary-care/",
    description: "Outpatient pulmonary evaluations for COPD, asthma, chronic cough, shortness of breath, and related breathing concerns.",
    gradient: "from-[var(--chart-2)] to-[var(--chart-3)]",
    orbColor: "var(--chart-2)"
  }, {
    icon: Moon,
    title: "Sleep Apnea Evaluation & CPAP Support",
    link: "/sleep-medicine/",
    description: "Evaluation and follow-up care for sleep apnea, CPAP use, mask tolerance, and ongoing sleep-related breathing concerns.",
    gradient: "from-[var(--chart-3)] to-[var(--chart-4)]",
    orbColor: "var(--chart-3)"
  }, {
    icon: Lungs,
    title: "COPD & Asthma Care",
    link: "/pulmonary-care/",
    description: "Ongoing pulmonary care for adults managing COPD, asthma, wheezing, breathing changes, and flare-up concerns.",
    gradient: "from-[var(--chart-4)] to-[var(--chart-1)]",
    orbColor: "var(--chart-4)"
  }, {
    icon: Lungs,
    title: "Chronic Cough & Shortness of Breath",
    link: "/pulmonary-care/",
    description: "Evaluation of persistent cough, unexplained shortness of breath, and symptoms that may need pulmonary care.",
    gradient: "from-[var(--chart-1)] to-[var(--chart-3)]",
    orbColor: "var(--chart-1)"
  }, {
    icon: Lungs,
    title: "Oxygen Therapy Management",
    link: "/pulmonary-care/",
    description: "Pulmonary follow-up for adults who use oxygen therapy or need monitoring for oxygen needs.",
    gradient: "from-[var(--chart-1)] to-[var(--chart-2)]",
    orbColor: "var(--chart-1)"
  }, {
    icon: Hospital,
    title: "Post-Hospital Pulmonary Follow-Up",
    link: "/pulmonary-care/",
    description: "Follow-up pulmonary care after hospitalization for respiratory illness, pneumonia, COPD flare-ups, or breathing concerns.",
    gradient: "from-[var(--chart-2)] to-[var(--chart-4)]",
    orbColor: "var(--chart-2)"
  }];
  return <section data-section-id="183" id="our-clinical-focus" className="dark relative overflow-hidden bg-background py-20 sm:py-28 lg:py-32">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-md opacity-20 blur-3xl" style={{
      background: "radial-gradient(circle, var(--chart-2) 0%, transparent 70%)"
    }} aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="inline-flex items-center gap-2 rounded-md border border-border bg-card/50 px-4 py-1.5 backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5" style={{
            color: "var(--chart-2)"
          }} aria-hidden="true" />
            <span className="font-mono text-xs font-bold uppercase tracking-widest text-muted-foreground">
              Adult Pulmonary & Sleep Medicine
            </span>
          </div>
          <h2 className="font-default mt-6 text-2xl font-medium tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Adult Pulmonary & Sleep Medicine Services
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground font-default">
            Care for breathing concerns, chronic lung conditions, sleep apnea, and related respiratory symptoms through outpatient pulmonary and sleep medicine visits.
          </p>
        </div>

        <div ref={gridRef} className={`mt-16 grid grid-cols-1 gap-5 transition-all duration-1000 sm:mt-20 md:grid-cols-2 lg:grid-cols-3 lg:gap-6 ${gridVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {smallCards.map((card, i) => {
          const Icon = card.icon;
          return <a href={card.link} key={card.title} data-index={i} className="group relative col-span-1 overflow-hidden rounded-md border border-border bg-card p-8 transition-all duration-500 hover:-translate-y-1 hover:border-transparent" style={{
            boxShadow: "var(--shadow-md)"
          }}>
                <div className={`pointer-events-none absolute inset-0 rounded-md bg-gradient-to-br ${card.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-100`} style={{
              padding: "1px",
              WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
              WebkitMaskComposite: "xor",
              maskComposite: "exclude"
            }} aria-hidden="true" />

                <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-md opacity-20 blur-2xl transition-all duration-500 group-hover:opacity-50" style={{
              background: `radial-gradient(circle, ${card.orbColor} 0%, transparent 70%)`
            }} aria-hidden="true" />

                <div className="relative">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-md bg-gradient-to-br ${card.gradient}`} style={{
                boxShadow: `0 8px 24px -8px ${card.orbColor}`
              }}>
                    <Icon className="h-6 w-6 text-primary-foreground" strokeWidth={2} aria-hidden="true" />
                  </div>

                  <h3 className="font-default mt-6 text-xl font-semibold tracking-tight text-foreground">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground font-default">
                    {card.description}
                  </p>
                </div>
              </a>;
        })}
        </div>
      </div>
    </section>;
}